import React, { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from 'react-intl';
import messages from 'common/messages/messages';
import profileMessages from '../../messages';
import notesMessages from '../../components/Notes/messages/notes';
import { DialogComponent, NumberTextField, TextArea } from 'common/components/index';
import { Controller, useForm } from 'react-hook-form';
import { defaultNumberFormatProps } from 'common/components/NumberController/NumberController';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { Box } from '@material-ui/core';
import { IFormReward, IReward } from '../../interfaces';
import { useDispatch } from 'react-redux';
import {
  resetNoteCodes,
  searchNoteCodesThunk,
  updatePersonRewardPoints,
} from '../../state/actions';
import { usePersonSelectorTemplate } from '../../hooks/usePersonSelector';
import {
  selectIsUpdateRewardsLoading,
  selectNoteCodes,
  selectNoteCodesLoading,
} from '../../state/selectors';
import { getRewardValidationSchema } from '../../utils';
import { PeakModules } from 'common/constants/peakModules';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import NoteCodeSelector from 'common/components/NoteCodeSelector/NoteCodeSelector';

interface ITopUpRewardsModal {
  onClose: () => void;
  personId: number;
  personRewards: number;
  module: PeakModules;
}

const TopUpRewardsModal: FC<ITopUpRewardsModal> = ({
  onClose,
  personId,
  personRewards,
  module,
}) => {
  const { MEMBER_REWARD_POINT_EDIT } = usePermissionContext();
  const renderIntlMessage = useRenderIntlMessage();
  const dispatch = useDispatch();
  const intl = useIntl();
  const usePersonSelector = usePersonSelectorTemplate(personId);
  const isUpdateRewardsLoading: boolean = usePersonSelector(selectIsUpdateRewardsLoading);
  const noteCodesIsLoading = usePersonSelector(selectNoteCodesLoading);
  const noteCodes = usePersonSelector(selectNoteCodes);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormReward>({
    defaultValues: {
      noteText: '',
      points: Number(personRewards),
      noteCode: null,
    },
    resolver: yupResolver(getRewardValidationSchema()) as any, // TODO - PRM-3575 need types
    mode: 'onSubmit',
  });

  const onSubmit = (formReward: IFormReward) => {
    const reward: IReward = {
      points: formReward.points,
      note: {
        text: formReward.noteText,
        noteCodeId: formReward.noteCode?.id || '',
      },
    };

    dispatch(updatePersonRewardPoints(personId, reward, onClose, module));
  };

  const clearSearchResults = () => {
    dispatch(resetNoteCodes(null, personId));
  };

  const searchNoteCodes = (search: string) => {
    dispatch(searchNoteCodesThunk(search, personId));
  };

  return (
    <DialogComponent
      size="xs"
      title={intl.formatMessage(profileMessages.rewardsTitle)}
      submitBtnTitle={intl.formatMessage(messages.saveBtn)}
      isOpen
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      disableFullScreenBehavior
      loading={isUpdateRewardsLoading}
      disabled={isUpdateRewardsLoading}
      submitBtn={MEMBER_REWARD_POINT_EDIT}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="points"
          render={({ field }) => (
            <NumberTextField
              value={field.value}
              onChange={v => field.onChange(v || 0)}
              variant="outlined"
              onBlur={field.onBlur}
              fullWidth
              label={intl.formatMessage(profileMessages.amountRewardsLabel)}
              numberFormatProps={{
                ...defaultNumberFormatProps,
                maxLength: 11,
              }}
              InputProps={{
                readOnly: !MEMBER_REWARD_POINT_EDIT,
              }}
              error={!!errors.points}
              helperText={renderIntlMessage(errors.points?.message)}
            />
          )}
        />

        <Box mt={2}>
          <Controller
            name="noteText"
            control={control}
            render={({ field }) => (
              <TextArea
                value={field.value}
                label={intl.formatMessage(notesMessages.note)}
                variant="outlined"
                autoComplete="none"
                fullWidth
                minRows={1}
                maxRows={6}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={!!errors.noteText}
                helperText={renderIntlMessage(errors?.noteText?.message)}
                maxSymbols={255}
                InputProps={{
                  readOnly: !MEMBER_REWARD_POINT_EDIT,
                }}
              />
            )}
          />
        </Box>

        <Box mt={2}>
          <Controller
            control={control}
            name="noteCode"
            render={({ field: { value, onChange } }) => {
              return (
                <NoteCodeSelector
                  searchNoteCodes={searchNoteCodes}
                  profileId={personId}
                  onChange={v => onChange(v)}
                  value={value}
                  clearSearchResults={clearSearchResults}
                  noteCodes={noteCodes.toJS()}
                  error={!!errors.noteCode}
                  helperText={renderIntlMessage(errors.noteCode?.message)}
                  isLoading={noteCodesIsLoading}
                  readOnly={!MEMBER_REWARD_POINT_EDIT}
                />
              );
            }}
          />
        </Box>
      </form>
    </DialogComponent>
  );
};

export default TopUpRewardsModal;
